import React, { useState, useEffect, useMemo } from 'react'
import axios from 'axios'
import { useBrand } from '../context/BrandContext'
import { useNavigate } from 'react-router-dom'
import { colors, radius, s, ui, chartBase, lineGradient, areaGradient } from '../styles/theme'
import Chart from '../components/Chart'

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000'

const style = {
  card: { ...s.cardSm, padding: '24px' },
  th: { ...s.th, padding: '8px 10px' },
  td: { ...s.td, padding: '8px 10px', borderBottom: 'none' },
  input: { ...s.input, padding: '10px', fontSize: '14px' },
  label: { ...s.label, marginBottom: '6px' },
  select: { ...s.select, padding: '10px', fontSize: '14px' }
}

const axisBase = {
  axisLine: { lineStyle: { color: colors.border } },
  axisTick: { show: false },
  axisLabel: { color: colors.textMuted, fontSize: 11 },
  splitLine: { lineStyle: { color: colors.borderLight } },
}

const FUNNEL_STEPS = ['open_app', 'login', 'product_view', 'category_view', 'checkout_start', 'purchase']

const say = (v) => Number(v || 0).toLocaleString('tr-TR')

const gunEtiketi = (v) => {
  const t = new Date(v)
  return isNaN(t.getTime()) ? String(v).slice(5, 10) : t.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' })
}

const EVENT_LABELS = {
  open_app: 'Siteye Geldi',
  login: 'Giriş Yaptı',
  product_view: 'Ürün Görüntüledi',
  category_view: 'Kategori Görüntüledi',
  checkout_start: 'Sepet Sayfası',
  purchase: 'Satın Aldı'
}

function SmartEvent() {
  const { selectedBrand } = useBrand()
  const navigate = useNavigate()
  const [tab, setTab] = useState('stats')
  const [userDetail, setUserDetail] = useState(null)
  const token = localStorage.getItem('adminToken')
  const headers = { Authorization: token }

  if (!selectedBrand) return (
    <div style={{ textAlign: 'center', padding: '60px' }}>
      <p style={{ ...ui.notice, maxWidth: '360px', margin: '0 auto 16px' }}>Önce bir marka seçin.</p>
      <button onClick={() => navigate('/brands')} style={s.btnPrimary}>Markalara Git</button>
    </div>
  )

  if (userDetail) {
    return <UserDetailView
      brandId={selectedBrand.id}
      email={userDetail}
      headers={headers}
      onBack={() => setUserDetail(null)}
    />
  }

  return (
    <div>
      <div className="sx-wrap" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '24px' }}>
        <h2 style={s.pageTitle}>Smart Event — {selectedBrand.name}</h2>
        <div style={{ ...ui.tabBar, marginBottom: 0, borderBottom: 'none', gap: '22px' }}>
          {[
            { key: 'stats', label: ' İstatistikler' },
            { key: 'top', label: ' En Çok Görüntülenen' },
            { key: 'segment', label: ' Segment Sorgula' }
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              ...ui.tab(tab === t.key),
              marginRight: 0
            }}>{t.label}</button>
          ))}
        </div>
      </div>

      {tab === 'stats' && <StatsTab brandId={selectedBrand.id} headers={headers} onUserClick={setUserDetail} />}
      {tab === 'top' && <TopTab brandId={selectedBrand.id} headers={headers} onUserClick={setUserDetail} />}
      {tab === 'segment' && <SegmentTab brandId={selectedBrand.id} headers={headers} onUserClick={setUserDetail} />}
    </div>
  )
}

function StatsTab({ brandId, headers, onUserClick }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().split('T')[0]
  })
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0])


  useEffect(() => { fetchStats() }, [brandId]) // eslint-disable-line

  function fetchStats() {
    setLoading(true)
    axios.get(`${API}/admin/brands/${brandId}/smart-event/stats?date_from=${dateFrom}&date_to=${dateTo}`, { headers })
      .then(res => { setData(res.data); setLoading(false) })
      .catch(() => setLoading(false))
  }

  const dailyOption = useMemo(() => {
    const g = (data && data.daily) || []
    return {
      tooltip: {
        ...chartBase.tooltip,
        formatter: (ps) => {
          const p = ps[0]
          const row = g[p.dataIndex] || {}
          const t = new Date(row.date)
          const tarih = isNaN(t) ? String(row.date) : t.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' })
          return tarih + '<br/>Tekil kullanıcı: <b>' + say(row.unique_users) + '</b>'
        },
      },
      grid: { left: 8, right: 16, top: 16, bottom: 8, containLabel: true },
      xAxis: {
        type: 'category', boundaryGap: false,
        data: g.map(x => gunEtiketi(x.date)),
        ...axisBase, splitLine: { show: false },
      },
      yAxis: { type: 'value', ...axisBase, minInterval: 1 },
      series: [{
        name: 'Tekil kullanıcı', type: 'line', smooth: true,
        showSymbol: false, symbol: 'circle', symbolSize: 7,
        data: g.map(x => Number(x.unique_users) || 0),
        lineStyle: { width: 2, color: lineGradient },
        itemStyle: { color: colors.primary, borderColor: colors.bgCard, borderWidth: 2 },
        areaStyle: { color: areaGradient },
        emphasis: { focus: 'series' },
      }],
    }
  }, [data])

  const funnelOption = useMemo(() => {
    const byType = {}
    ;((data && data.by_event) || []).forEach(e => { byType[e.event_type] = parseInt(e.unique_users) || 0 })
    const ilk = byType[FUNNEL_STEPS[0]] || 0
    const adimlar = FUNNEL_STEPS.map((step, i) => {
      const val = byType[step] || 0
      const onceki = i > 0 ? (byType[FUNNEL_STEPS[i - 1]] || 0) : val
      return {
        ad: EVENT_LABELS[step] || step,
        deger: val,
        adim: i,
        // adımlar iç içe olmadığı için düşüş yalnız gerçekten azaldığında anlamlı
        dusus: (i > 0 && onceki > val) ? Math.round(((onceki - val) / onceki) * 100) : null,
        ilkOran: ilk > 0 ? Math.round((val / ilk) * 100) : 0,
      }
    })
    // kategori ekseni alttan yukarı çizer; yolculuk sırası üstte kalsın diye ters çevir
    const ters = adimlar.slice().reverse()
    return {
      tooltip: {
        ...chartBase.tooltip,
        axisPointer: { type: 'shadow' },
        formatter: (ps) => {
          const d = ters[ps[0].dataIndex]
          let t = '<b>' + d.ad + '</b><br/>' + say(d.deger) + ' kişi'
          t += '<br/>İlk adıma göre: %' + d.ilkOran
          if (d.dusus != null) t += '<br/>Önceki adımdan düşüş: %' + d.dusus
          return t
        },
      },
      grid: { left: 16, right: 64, top: 8, bottom: 8, containLabel: true },
      xAxis: { type: 'value', ...axisBase, minInterval: 1 },
      yAxis: {
        type: 'category',
        data: ters.map(x => x.ad),
        ...axisBase,
        splitLine: { show: false },
        axisLabel: { color: colors.textSecondary, fontSize: 12 },
      },
      series: [{
        type: 'bar',
        barMaxWidth: 22,
        data: ters.map((x, i) => ({
          value: x.deger,
          itemStyle: {
            color: colors.primary,
            opacity: Math.max(0.45, 0.5 + (i / Math.max(ters.length - 1, 1)) * 0.5),
            borderRadius: [0, 4, 4, 0],
          },
        })),
        label: {
          show: true, position: 'right', fontSize: 11,
          color: colors.textSecondary,
          formatter: (p) => say(p.value),
        },
      }],
    }
  }, [data])

  return (
    <div>
      <div className="sx-wrap" style={{ ...style.card, marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 160px', minWidth: 0 }}>
          <label style={style.label}>Başlangıç</label>
          <input type="date" style={style.input} value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
        </div>
        <div style={{ flex: '1 1 160px', minWidth: 0 }}>
          <label style={style.label}>Bitiş</label>
          <input type="date" style={style.input} value={dateTo} onChange={e => setDateTo(e.target.value)} />
        </div>
        <button onClick={fetchStats} style={{ ...s.btnPrimary, padding: '10px 24px' }}>Filtrele</button>
      </div>

      {loading ? <p style={ui.emptyText}>Yükleniyor...</p> : !data ? <p style={ui.emptyText}>Veri alınamadı.</p> : (
        <>
          <div className="sx-grid-stat" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '20px' }}>
            {data.by_event.filter(e => EVENT_LABELS[e.event_type]).map(e => (
              <div key={e.event_type} style={ui.statBox}>
                <div style={{ ...ui.statValue, fontSize: '22px', margin: 0 }}>{parseInt(e.unique_users).toLocaleString('tr-TR')}</div>
                <div style={{ ...ui.statLabel, fontSize: '13px', color: colors.textPrimary, fontWeight: 600, margin: '4px 0 2px' }}>{EVENT_LABELS[e.event_type] || e.event_type}</div>
                <div style={{ ...ui.statLabel, color: colors.textMuted }}>{parseInt(e.total).toLocaleString('tr-TR')} toplam event</div>
              </div>
            ))}
          </div>

          {data.daily.length > 0 && (
            <div style={{ ...style.card, marginBottom: '20px' }}>
              <h3 style={{ ...s.sectionTitle, margin: '0 0 16px' }}> Günlük Tekil Kullanıcı</h3>
              <Chart option={dailyOption} height={typeof window !== 'undefined' && window.innerWidth <= 768 ? 210 : 260} />
            </div>
          )}

          <div style={style.card}>
            <h3 style={{ ...s.sectionTitle, margin: '0 0 16px' }}> Satış Hunisi</h3>
            <Chart option={funnelOption} height={typeof window !== 'undefined' && window.innerWidth <= 768 ? 220 : 280} />
          </div>
        </>
      )}
    </div>
  )
}

function TopTab({ brandId, headers, onUserClick }) {
  const [products, setProducts] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().split('T')[0]
  })
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0])

  useEffect(() => { fetchData() }, [brandId]) // eslint-disable-line

  function fetchData() {
    setLoading(true)
    Promise.all([
      axios.get(`${API}/admin/brands/${brandId}/smart-event/top-products?date_from=${dateFrom}&date_to=${dateTo}`, { headers }),
      axios.get(`${API}/admin/brands/${brandId}/smart-event/top-categories?date_from=${dateFrom}&date_to=${dateTo}`, { headers })
    ]).then(([p, c]) => {
      setProducts(p.data)
      setCategories(c.data)
      setLoading(false)
    }).catch(() => setLoading(false))
  }

  return (
    <div>
      <div className="sx-wrap" style={{ ...style.card, marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 160px', minWidth: 0 }}>
          <label style={style.label}>Başlangıç</label>
          <input type="date" style={style.input} value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
        </div>
        <div style={{ flex: '1 1 160px', minWidth: 0 }}>
          <label style={style.label}>Bitiş</label>
          <input type="date" style={style.input} value={dateTo} onChange={e => setDateTo(e.target.value)} />
        </div>
        <button onClick={fetchData} style={{ ...s.btnPrimary, padding: '10px 24px' }}>Filtrele</button>
      </div>

      {loading ? <p style={ui.emptyText}>Yükleniyor...</p> : (
        <div className="sx-grid-panel" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div style={style.card}>
            <h3 style={{ ...s.sectionTitle, margin: '0 0 16px' }}> En Çok Görüntülenen Ürünler</h3>
            {products.length === 0 ? <p style={ui.emptyText}>Veri yok.</p> : (
              <div className="sx-scroll-x">
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={style.th}>#</th>
                    <th style={style.th}>Ürün</th>
                    <th style={{ ...style.th, textAlign: 'right' }}>Tekil</th>
                    <th style={{ ...style.th, textAlign: 'right' }}>Toplam</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p, i) => (
                    <tr key={i} style={{ borderTop: `1px solid ${colors.borderLight}` }}>
                      <td style={{ ...style.td, ...ui.numMuted, padding: '8px 10px', fontWeight: 600 }}>{i + 1}</td>
                      <td style={{ ...style.td, maxWidth: '200px' }}>
                        <div style={{ fontWeight: 500, fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.product_name}</div>
                      </td>
                      <td style={{ ...style.td, ...ui.num, padding: '8px 10px', textAlign: 'right' }}>{parseInt(p.unique_users)}</td>
                      <td style={{ ...style.td, ...ui.numMuted, padding: '8px 10px', textAlign: 'right' }}>{parseInt(p.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>

          <div style={style.card}>
            <h3 style={{ ...s.sectionTitle, margin: '0 0 16px' }}> En Çok Görüntülenen Kategoriler</h3>
            {categories.length === 0 ? <p style={ui.emptyText}>Veri yok.</p> : (
              <div className="sx-scroll-x">
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={style.th}>#</th>
                    <th style={style.th}>Kategori</th>
                    <th style={{ ...style.th, textAlign: 'right' }}>Tekil</th>
                    <th style={{ ...style.th, textAlign: 'right' }}>Toplam</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((c, i) => (
                    <tr key={i} style={{ borderTop: `1px solid ${colors.borderLight}` }}>
                      <td style={{ ...style.td, ...ui.numMuted, padding: '8px 10px', fontWeight: 600 }}>{i + 1}</td>
                      <td style={{ ...style.td, fontWeight: 500, fontSize: '12px' }}>{c.category_name}</td>
                      <td style={{ ...style.td, ...ui.num, padding: '8px 10px', textAlign: 'right' }}>{parseInt(c.unique_users)}</td>
                      <td style={{ ...style.td, ...ui.numMuted, padding: '8px 10px', textAlign: 'right' }}>{parseInt(c.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function SegmentTab({ brandId, headers, onUserClick }) {
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().split('T')[0]
  })
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0])
  const [conditions, setConditions] = useState([{ event_type: 'product_view', has: true, category_code: '' }])
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)

  function addCondition() {
    setConditions([...conditions, { event_type: 'purchase', has: false, category_code: '' }])
  }

  function removeCondition(i) {
    setConditions(conditions.filter((_, idx) => idx !== i))
  }

  function updateCondition(i, field, value) {
    const updated = [...conditions]
    updated[i] = { ...updated[i], [field]: value }
    setConditions(updated)
  }

  async function runQuery() {
    setLoading(true); setResult(null)
    try {
      const res = await axios.post(`${API}/admin/brands/${brandId}/smart-event/query`, { date_from: dateFrom, date_to: dateTo, conditions }, { headers })
      setResult(res.data)
    } catch (e) {
      alert('Sorgu hatası: ' + (e.response?.data?.error || e.message))
    }
    setLoading(false)
  }

  async function exportCSV() {
    setExporting(true)
    try {
      const res = await axios.post(`${API}/admin/brands/${brandId}/smart-event/export`, { date_from: dateFrom, date_to: dateTo, conditions }, { headers, responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const a = document.createElement('a'); a.href = url
      a.download = `segment-${dateFrom}-${dateTo}.csv`; a.click()
      window.URL.revokeObjectURL(url)
    } catch (e) { alert('Export hatası') }
    setExporting(false)
  }

  return (
    <div>
      <div className="sx-wrap" style={{ ...style.card, marginBottom: '16px', display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 160px', minWidth: 0 }}>
          <label style={style.label}>Başlangıç Tarihi</label>
          <input type="date" style={style.input} value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
        </div>
        <div style={{ flex: '1 1 160px', minWidth: 0 }}>
          <label style={style.label}>Bitiş Tarihi</label>
          <input type="date" style={style.input} value={dateTo} onChange={e => setDateTo(e.target.value)} />
        </div>
      </div>

      <div style={{ ...style.card, marginBottom: '16px' }}>
        <div className="sx-wrap" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '16px' }}>
          <h3 style={{ ...s.sectionTitle, margin: 0 }}> Segment Koşulları</h3>
          <button onClick={addCondition} style={ui.btnSm}>+ Koşul Ekle</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {conditions.map((c, i) => (
            <div key={i} className="sx-wrap" style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', background: colors.bgPage, border: `1px solid ${colors.border}`, padding: '12px', borderRadius: radius.md }}>
              <select value={c.has ? 'true' : 'false'} onChange={e => updateCondition(i, 'has', e.target.value === 'true')}
                style={{ ...style.select, width: '140px', maxWidth: '100%', flex: '0 1 140px', fontWeight: 600 }}>
                <option value="true"> Yaptı</option>
                <option value="false"> Yapmadı</option>
              </select>
              <select value={c.event_type} onChange={e => updateCondition(i, 'event_type', e.target.value)} style={{ ...style.select, flex: '1 1 180px', minWidth: 0 }}>
                {Object.entries(EVENT_LABELS).map(([k, v]) => (<option key={k} value={k}>{v}</option>))}
              </select>
              <input style={{ ...style.input, width: '180px', maxWidth: '100%', flex: '0 1 180px' }} placeholder="Kategori kodu (opsiyonel)"
                value={c.category_code || ''} onChange={e => updateCondition(i, 'category_code', e.target.value)} />
              {conditions.length > 1 && (
                <button onClick={() => removeCondition(i)} style={{ ...ui.btnDangerQuiet, padding: '4px 10px', fontSize: '16px', lineHeight: 1.2 }}>×</button>
              )}
            </div>
          ))}
        </div>

        <div style={{ ...ui.notice, marginTop: '12px' }}>
          <strong>Örnek:</strong> "Ürün Görüntüledi (Yaptı) + Satın Aldı (Yapmadı)" → Ürün bakıp satın almayan kullanıcılar
        </div>
      </div>

      <div className="sx-wrap" style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
        <button onClick={runQuery} disabled={loading} style={{ ...s.btnPrimary, padding: '10px 28px', opacity: loading ? 0.7 : 1 }}>
          {loading ? 'Sorgulanıyor...' : ' Sorgula'}
        </button>
        {result && result.count > 0 && (
          <button onClick={exportCSV} disabled={exporting} style={{ ...s.btnSecondary, padding: '10px 24px' }}>
            {exporting ? 'İndiriliyor...' : ` CSV İndir (${result.count} kişi)`}
          </button>
        )}
      </div>

      {result && (
        <div style={style.card}>
          <div className="sx-wrap" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '16px' }}>
            <h3 style={{ ...s.sectionTitle, margin: 0 }}>
              Sonuç: <span style={{ ...ui.num, fontSize: '15px' }}>{result.count} kullanıcı</span>
            </h3>
          </div>

          {result.count === 0 ? (
            <p style={ui.emptyText}>Bu koşullarla eşleşen kullanıcı bulunamadı.</p>
          ) : (
            <div className="sx-scroll-x">
            <table className="sx-cards" style={s.table}>
              <thead>
                <tr>
                  <th style={{ ...style.th, padding: '10px' }}>E-posta</th>
                  <th style={{ ...style.th, padding: '10px' }}>Event Tipleri</th>
                  <th style={{ ...style.th, padding: '10px', textAlign: 'right' }}>Toplam Event</th>
                  <th style={{ ...style.th, padding: '10px' }}>Son Görülme</th>
                  <th style={{ ...style.th, padding: '10px' }}></th>
                </tr>
              </thead>
              <tbody>
                {result.emails.map((r, i) => (
                  <tr key={i} style={{ borderTop: `1px solid ${colors.borderLight}` }}>
                    <td data-label="E-posta" style={{ ...style.td, padding: '10px', fontWeight: 500 }}>{r.email}</td>
                    <td data-label="Event Tipleri" style={{ ...style.td, padding: '10px' }}>
                      {r.event_types.split(', ').map(et => (
                        <span key={et} style={{
                          ...ui.chip,
                          marginRight: '4px', marginBottom: '2px', fontSize: '11px'
                        }}>{EVENT_LABELS[et] || et}</span>
                      ))}
                    </td>
                    <td data-label="Toplam Event" style={{ ...style.td, ...ui.numMuted, padding: '10px', textAlign: 'right' }}>{r.event_count}</td>
                    <td data-label="Son Görülme" style={{ ...style.td, padding: '10px', color: colors.textMuted, fontSize: '12px' }}>
                      {new Date(r.last_seen).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td style={{ ...style.td, padding: '10px', textAlign: 'center' }}>
                      <button onClick={() => onUserClick(r.email)} style={ui.btnSm}>
                        Detay
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function UserDetailView({ brandId, email, headers, onBack }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [dateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().split('T')[0]
  })
  const [dateTo] = useState(new Date().toISOString().split('T')[0])

  useEffect(() => {
    axios.get(`${API}/admin/brands/${brandId}/smart-event/user/${encodeURIComponent(email)}?date_from=${dateFrom}&date_to=${dateTo}`, { headers })
      .then(res => { setData(res.data); setLoading(false) })
      .catch(() => setLoading(false))
  }, []) // eslint-disable-line

  return (
    <div>
      <button onClick={onBack} style={{ ...ui.btnSm, marginBottom: '20px' }}>
        ← Geri
      </button>

      <div style={{ ...style.card, marginBottom: '20px' }}>
        <h2 className="sx-break" style={{ ...s.pageTitle, margin: '0 0 4px', fontSize: '18px' }}>{email}</h2>
        <p style={s.pageSubtitle}>Son 30 gün</p>
      </div>

      {loading ? <p style={ui.emptyText}>Yükleniyor...</p> : !data ? <p style={ui.emptyText}>Veri alınamadı.</p> : (
        <div className="sx-grid-panel" style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '16px' }}>
          {/* Özet */}
          <div style={style.card}>
            <h3 style={{ ...s.sectionTitle, margin: '0 0 16px', fontSize: '13px', color: colors.textSecondary }}>ÖZET</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {data.summary.filter(s => EVENT_LABELS[s.event_type]).map(s => (
                <div key={s.event_type} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: colors.bgPage, border: `1px solid ${colors.borderLight}`, borderRadius: radius.md }}>
                  <span style={{ fontSize: '13px', color: colors.textSecondary }}>{EVENT_LABELS[s.event_type] || s.event_type}</span>
                  <span style={ui.num}>{parseInt(s.total)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Event timeline */}
          <div style={style.card}>
            <h3 style={{ ...s.sectionTitle, margin: '0 0 16px', fontSize: '13px', color: colors.textSecondary }}>AKTİVİTE GEÇMİŞİ ({data.events.length})</h3>
            <div style={{ maxHeight: '500px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {data.events.filter(e => EVENT_LABELS[e.event_type]).map((e, i) => (
                <div key={i} style={{ display: 'flex', gap: '12px', padding: '10px 12px', background: colors.bgPage, border: `1px solid ${colors.borderLight}`, borderRadius: radius.md, alignItems: 'flex-start' }}>
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: colors.textMuted, marginTop: '6px', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>{EVENT_LABELS[e.event_type] || e.event_type}</span>
                      <span style={{ fontSize: '11px', color: colors.textMuted, flexShrink: 0, marginLeft: '8px' }}>
                        {new Date(e.created_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    {e.product_name && <div className="sx-break" style={{ fontSize: '12px', color: colors.textSecondary, marginTop: '2px' }}> {e.product_name}</div>}
                    {e.category_name && <div className="sx-break" style={{ fontSize: '12px', color: colors.textSecondary, marginTop: '2px' }}> {e.category_name}</div>}
                    {e.meta && e.meta.order_code && (
                      <div>
                        <div style={{ ...ui.num, fontSize: '12px', marginTop: '2px' }}>
                           Sipariş: {e.meta.order_code} — {parseFloat(e.meta.amount || 0).toLocaleString('tr-TR')} TL
                        </div>
                        {e.meta.products && e.meta.products.length > 0 && (
                          <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {e.meta.products.map((p, pi) => (
                              <div key={pi} style={{ display: 'flex', gap: '8px', alignItems: 'center', background: colors.bgCard, padding: '6px 8px', borderRadius: radius.sm, border: `1px solid ${colors.border}` }}>
                                {p.image_url && <img src={p.image_url} alt={p.name} style={{ width: '36px', height: '36px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0 }} />}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <a href={p.product_url} target="_blank" rel="noreferrer" style={{ fontSize: '12px', color: colors.textPrimary, fontWeight: 500, textDecoration: 'none', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {p.name}
                                  </a>
                                  <div style={{ fontSize: '11px', color: colors.textSecondary, marginTop: '2px', fontVariantNumeric: 'tabular-nums' }}>
                                    {p.quantity > 1 ? `${p.quantity} adet × ` : ''}{parseFloat(p.price).toLocaleString('tr-TR')} TL
                                    {p.brand && ` · ${p.brand}`}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {e.page_url && !e.meta?.order_code && <div style={{ fontSize: '11px', color: colors.textMuted, marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.page_url}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SmartEvent